import { useAuth0 } from "@auth0/auth0-react";
import { InvitationCode, InvitationCodeFormData } from "../model/invitation_code";

export function useInvitationCodeAPI() {
    const { getAccessTokenSilently } = useAuth0();

    const getCode = (id: string): Promise<InvitationCode> => {
        return new Promise((resolve, reject) => {
            getAccessTokenSilently().then((accessToken) => {
                fetch(`${process.env.REACT_APP_API_URL}/invitation-code/admin/${id}`, {
                    headers: {
                        authorization: `Bearer ${accessToken}`,
                    },
                })
                    .then((response) => {
                        if (response.status === 200) {
                            response.json().then((data: InvitationCode) => resolve(data)).catch(reject);
                        } else {
                            reject(new Error("Failed to fetch invitation code: " + response.statusText));
                        }
                    })
                    .catch(reject);
            }).catch(reject);
        });
    };

    const createCode = (payload: InvitationCodeFormData): Promise<InvitationCode> => {
        return new Promise((resolve, reject) => {
            getAccessTokenSilently().then((accessToken) => {
                fetch(`${process.env.REACT_APP_API_URL}/invitation-code/admin`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        authorization: `Bearer ${accessToken}`,
                    },
                    body: JSON.stringify(payload),
                })
                    .then((response) => {
                        if (response.status === 200) {
                            response.json().then((data: InvitationCode) => resolve(data)).catch(reject);
                        } else {
                            reject(new Error("Failed to create invitation code: " + response.statusText));
                        }
                    })
                    .catch(reject);
            }).catch(reject);
        });
    };

    const updateCode = (id: string, payload: Partial<InvitationCodeFormData>): Promise<InvitationCode> => {
        return new Promise((resolve, reject) => {
            getAccessTokenSilently().then((accessToken) => {
                fetch(`${process.env.REACT_APP_API_URL}/invitation-code/admin/${id}`, {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                        authorization: `Bearer ${accessToken}`,
                    },
                    body: JSON.stringify(payload),
                })
                    .then((response) => {
                        if (response.status === 200) {
                            response.json().then((data: InvitationCode) => resolve(data)).catch(reject);
                        } else {
                            reject(new Error("Failed to update invitation code: " + response.statusText));
                        }
                    })
                    .catch(reject);
            }).catch(reject);
        });
    };

    const getCodes = (): Promise<InvitationCode[]> => {
        return new Promise((resolve, reject) => {
            getAccessTokenSilently().then((accessToken) => {
                fetch(`${process.env.REACT_APP_API_URL}/invitation-code/admin/list`, {
                    headers: {
                        authorization: `Bearer ${accessToken}`,
                    },
                })
                    .then((response) => {
                        if (response.status === 200) {
                            response.json().then((data: InvitationCode[]) => resolve(data)).catch(reject);
                        } else {
                            reject(new Error("Failed to fetch invitation codes: " + response.statusText));
                        }
                    })
                    .catch(reject);
            }).catch(reject);
        });
    };

    const deleteCode = (id: string): Promise<void> => {
        return new Promise((resolve, reject) => {
            getAccessTokenSilently().then((accessToken) => {
                fetch(`${process.env.REACT_APP_API_URL}/invitation-code/admin/${id}`, {
                    method: "DELETE",
                    headers: {
                        authorization: `Bearer ${accessToken}`,
                    },
                })
                    .then((response) => {
                        if (response.status === 200) {
                            resolve();
                        } else {
                            reject(new Error("Failed to delete invitation code: " + response.statusText));
                        }
                    })
                    .catch(reject);
            }).catch(reject);
        });
    };

    const disableCode = (id: string): Promise<void> => {
        return new Promise((resolve, reject) => {
            getAccessTokenSilently().then((accessToken) => {
                fetch(`${process.env.REACT_APP_API_URL}/invitation-code/admin/${id}/disable`, {
                    method: "PUT",
                    headers: {
                        authorization: `Bearer ${accessToken}`,
                    },
                })
                    .then((response) => {
                        if (response.status === 200) {
                            resolve();
                        } else {
                            reject(new Error("Failed to disable invitation code: " + response.statusText));
                        }
                    })
                    .catch(reject);
            }).catch(reject);
        });
    };

    return { getCode, createCode, updateCode, getCodes, deleteCode, disableCode };
}
